import { useState, useCallback } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { AddContext, isVisualAsset } from 'screens/ContentPlan/CreateCampaignModalV2/AddContext'
import { CampaignSource, SourceRole } from 'screens/ContentPlan/CreateCampaignModalV2/Types'
import { useCreateFloatingContextSource } from 'hooks/queries/schedules/useCreateFloatingContextSource'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { AlmanacFile, SourceMaterialsMediaObject } from 'types'
import { ContextSourceEntry } from './Types'
import Styles from './AddContextModal.module.scss'

interface AddContextModalProps {
  /** Required to persist sources as FloatingContextSource records. When omitted, sources are
   * returned directly without creating any server-side records (useful for flows that handle
   * persistence themselves, e.g. regenerate content). */
  scheduleId?: number
  existingSources: ContextSourceEntry[]
  onSubmit: (allContextSourceIds: number[], allEntries: ContextSourceEntry[]) => void
  brandKitId?: number
  brandKitMediaEnabled?: boolean
  title?: string
  submitButtonLabel?: string
  acceptMedia?: boolean
}

export const AddContextModal = ({
  scheduleId,
  existingSources,
  onSubmit,
  brandKitId,
  brandKitMediaEnabled,
  title = 'Add websites or documents specific to this post',
  submitButtonLabel = 'Add Context & Regenerate Topic',
  acceptMedia = false,
}: AddContextModalProps) => {
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const uploadsProjectKey = uploadsProjectQuery.data?.project?.id ?? null
  const [sources, setSources] = useState<CampaignSource[]>(() =>
    existingSources.map((es) => ({ source: es.source, roles: ['source_material'] as SourceRole[] })),
  )
  const [isUploading, setIsUploading] = useState(false)
  const [isAnalysisPending, setIsAnalysisPending] = useState(false)
  const { mutateAsync: createContextSource, isLoading: isCreating } = useCreateFloatingContextSource()

  const handleSubmit = useCallback(async () => {
    const existingSourceIds = new Set(existingSources.map((es) => es.source.id))

    // Keep existing entries that are still present in the current list
    const currentSourceIds = new Set(sources.map((cs) => cs.source.id))
    const keptEntries = existingSources.filter((es) => currentSourceIds.has(es.source.id))

    // For new sources: persist as FloatingContextSource records when a scheduleId is provided,
    // otherwise fall back to using the source's own id as a placeholder.
    const newSources = sources.filter((cs) => !existingSourceIds.has(cs.source.id))
    const newEntries: ContextSourceEntry[] = await Promise.all(
      newSources.map(async (cs) => {
        if (isVisualAsset(cs.source) && uploadsProjectKey) {
          const media = cs.source as SourceMaterialsMediaObject
          let mediaFileId = cs.mediaFileId
          if (!mediaFileId) {
            const res = await MediaFileClient.create(workspaceId, {
              source_url: media.source_url,
              mime_type: media.mime_type,
              title: media.source_url.split('/').pop() ?? 'upload',
              project_key: uploadsProjectKey,
            })
            if (res.type === SUCCESS_RESPONSE_TYPE) {
              const { almanac_file } = res.data as { almanac_file: AlmanacFile }
              mediaFileId = almanac_file.media_file_id
            }
          }
          return { contextSourceId: cs.source.id as number, source: cs.source, mediaFileId }
        }
        const contextSourceId = scheduleId
          ? await createContextSource({ scheduleId, sourceId: cs.source.id, sourceType: cs.source.type })
          : (cs.source.id as number)
        return { contextSourceId, source: cs.source }
      }),
    )

    const allEntries = [...keptEntries, ...newEntries]
    onSubmit(
      allEntries.map((e) => e.contextSourceId),
      allEntries,
    )
    closeModal()
  }, [sources, existingSources, scheduleId, workspaceId, uploadsProjectKey, createContextSource, onSubmit, closeModal])

  const canSubmit = sources.length > 0 && !isUploading && !isCreating && !isAnalysisPending
  let submitLabel = canSubmit ? submitButtonLabel : 'Add Context'
  if (isCreating) submitLabel = 'Saving...'

  return (
    <Modal.Root size="sm" blaze onPressOutside={closeModal} aria-label="Add context for topic">
      <Modal.Header>
        <Heading level={2}>{title}</Heading>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.body}>
          <AddContext
            brandKitId={brandKitId}
            sources={sources}
            setSources={setSources}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onAnalysisPendingChange={setIsAnalysisPending}
            acceptMedia={acceptMedia}
            brandKitMediaEnabled={brandKitMediaEnabled}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={closeModal} size="lg">
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" onPress={handleSubmit} isDisabled={!canSubmit} size="lg">
            {submitLabel}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
